// Ubah respons /series/aggregated jadi bentuk `summary.{type}.data` punya
// endpoint lama, supaya buildFmsSeries dan komponen chart gak perlu diubah
// sama sekali. Sengaja nol import biar bisa dijalanin langsung pakai node.

// `additional` isinya {FUNGSI}_{fieldDB}_{indeks}, mis. AVG_dlrpRpm_0.
// Kalau gak diminta lewat functionNames, backend ngasihnya array kosong.
// Pemanggil selalu minta persis satu fungsi per prefix per panggilan, jadi
// match pertama gak ambigu. Kalau itu berubah, helper perlu field name juga.
function pickAggregated(row, prefix) {
  const additional = row && row.additional;
  if (!additional || Array.isArray(additional)) return undefined;
  const hit = Object.keys(additional).find((key) => key.startsWith(prefix));
  return hit === undefined ? undefined : additional[hit];
}

// Indeks di nama key itu posisi fungsi di functionNames punya endpoint yang
// ngirim, jadi key yang sama muncul dengan indeks beda di dua endpoint.
// Dicocokin lewat nama fungsi + field, indeksnya dipaksa ke versi legacy.
const LEGACY_AGGREGATE_KEYS = {
  LAST_dlgpLatitude: 'LAST_dlgpLatitude_4',
  LAST_dlgpLongitude: 'LAST_dlgpLongitude_5',
  SUM_dlgpDistance: 'SUM_dlgpDistance_3',
  AVG_dlgpWaveHeight: 'AVG_dlgpWaveHeight_9',
  AVG_dlgpWavePeriod: 'AVG_dlgpWavePeriod_13',
  AVG_dltmTemp: 'AVG_dltmTemp_2',
};

function normalizeAggregateKeys(additional) {
  if (!additional || Array.isArray(additional)) return undefined;
  const out = {};
  Object.keys(additional).forEach((key) => {
    const base = key.replace(/_\d+$/, '');
    out[LEGACY_AGGREGATE_KEYS[base] || key] = additional[key];
  });
  return out;
}

function groupByTimestamp(rows) {
  const out = {};
  (rows || []).forEach((row) => {
    if (!row || row.timestamp === undefined) return;
    const ts = String(row.timestamp);
    if (!out[ts]) out[ts] = [];
    out[ts].push(row);
  });
  return out;
}

export function seriesToSummaryData(type, rows, labelByUid = {}) {
  const buckets = groupByTimestamp(rows);
  const out = {};
  // Dipakai rpm/ae: aggregate yang diminta tapi gak ada di respons cuma perlu
  // di-warn sekali per panggilan, bukan sekali per baris.
  let warnedMissingAggregate = false;

  Object.keys(buckets).forEach((ts) => {
    const group = buckets[ts];

    if (type === 'rpm') {
      const bucket = {};
      group.forEach((row) => {
        if (!row.type) return;
        const avg = pickAggregated(row, 'AVGGT_');
        if (avg === undefined && !warnedMissingAggregate) {
          console.warn('[rollup chart] rpm: AVGGT_ yang diminta gak ada di respons, fallback ke sampel dasar');
          warnedMissingAggregate = true;
        }
        bucket[row.type] = avg === undefined ? row : { ...row, rpm: avg };
      });
      out[ts] = bucket;
      return;
    }

    if (type === 'ae') {
      const bucket = {};
      group.forEach((row) => {
        // `no` 0-based di respons, chart pakai AE1/AE2.
        const no = Number(row.no);
        if (!Number.isInteger(no) || no < 0) return;
        const key = `AE${no + 1}`;
        const runningSeconds = pickAggregated(row, 'SUM_');
        if (runningSeconds === undefined && !warnedMissingAggregate) {
          console.warn('[rollup chart] ae: SUM_ yang diminta gak ada di respons, runningSeconds jadi undefined');
          warnedMissingAggregate = true;
        }
        // `.rpm` diteruskan apa adanya, gak ada consumer yang baca field ini
        // dari jalur summary. Kalau nanti chart AE rpm butuh, koreksi AVG dulu.
        bucket[key] = { ...row, runningSeconds };
      });
      out[ts] = bucket;
      return;
    }

    if (type === 'flowmeter') {
      const bucket = {};
      group.forEach((row) => {
        const label = labelByUid[row.deviceId];
        if (!label) return;
        bucket[label] = row;
        // RPM/Fuel/Speed baca skalar {NAME}_FuelCons. Nama group = label tanpa
        // suffix _IN/_OUT, samain sama aturan store/asset.js: default-nya IN,
        // cuma _OUT yang dikecualikan. Endpoint lama ngisi skalar ini dengan
        // volumeFlowrate yang sama persis, TAPI itu cuma terverifikasi di aset
        // yang flowmeternya semua _IN. Belum ada aset in/out yang bisa dicek,
        // jadi belum terbukti volumeFlowrate (gross IN) ini masih sama dengan
        // _FuelCons legacy (net) begitu ada line _OUT.
        const groupName = label.replace(/_(IN|OUT)$/, '');
        if (!label.endsWith('_OUT')) {
          bucket[`${groupName}_FuelCons`] = row.volumeFlowrate;
        }
      });
      out[ts] = bucket;
      return;
    }

    if (type === 'temperature') {
      // Endpoint lama nyusun suhu bersarang {grup: {posisi: [baris]}}. Barisnya
      // sendiri gak bawa penanda grup (cuma `type` + `no`) — grupnya ada di
      // DEVICE-nya: devcLabel 'TEMPERATURE' (reservoir) vs 'EXHAUST_TEMPERATURE'.
      const bucket = {};
      group.forEach((row) => {
        if (!row.type) return;
        const avg = pickAggregated(row, 'AVG_');
        if (avg === undefined && !warnedMissingAggregate) {
          console.warn('[rollup chart] temperature: AVG_ yang diminta gak ada di respons, suhu jadi kosong');
          warnedMissingAggregate = true;
        }
        const grup = labelByUid[row.deviceId] || 'TEMPERATURE';
        if (!bucket[grup]) bucket[grup] = {};
        bucket[grup][row.type] = [{ ...row, additional: normalizeAggregateKeys(row.additional) || {} }];
      });
      out[ts] = bucket;
      return;
    }

    // gps (dan tipe lain kalau nanti nambah): satu baris per bucket. Diasumsikan
    // cuma satu device GPS per aset — aset multi-GPS bakal diam-diam cuma nyimpen
    // baris yang datang duluan (group[0]), sisanya kebuang.
    const row = group[0];
    // Field-nya WAJIB disebut, jangan prefix 'AVG_' doang: jalur Datalog juga
    // minta AVG buat wave height & period, dan match prefix bakal nimpa speed
    // pakai tinggi gelombang tanpa error.
    const avg = pickAggregated(row, 'AVG_dlgpSpeed');
    const base = avg === undefined ? row : { ...row, speed: avg };
    const additional = normalizeAggregateKeys(row.additional);
    out[ts] = additional ? { ...base, additional } : base;
  });

  return out;
}

// Endpoint /series/aggregated punya batas keras `limit: 6000` BARIS, dan kalau
// kena dia motong diam-diam: `count` mentok 6000, sisanya hilang tanpa error.
// Terverifikasi: 90 hari flowmeter 3 device @HOUR = 6.480 baris diharapkan,
// yang balik cuma 6.000.
// Baris = bucket x channel, jadi pemecahnya harus dihitung dari anggaran baris,
// bukan dari jumlah hari.
export function buildChunkRanges(start, end, chunkSeconds) {
  if (!(chunkSeconds > 0) || !(end > start)) return [{ start, end }];
  const out = [];
  let cursor = start;
  while (cursor < end) {
    const next = Math.min(cursor + chunkSeconds, end);
    out.push({ start: cursor, end: next });
    cursor = next;
  }
  return out;
}

// Tipe RPM (PORT/STARBOARD/CENTER) dari key summary RPM yang beneran ada datanya.
export function deriveRpmTypesFromSummary(summary) {
  const avg = summary?.rpm?.summary?.average || {};
  const running = summary?.rpm?.summary?.runningTime || {};
  const types = new Set([...Object.keys(avg), ...Object.keys(running)]);
  const order = ['PORT', 'STARBOARD', 'CENTER'];
  return Array.from(types).sort((a, b) => {
    const ia = order.indexOf(a);
    const ib = order.indexOf(b);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });
}

// aeCount = nomor AE tertinggi yang muncul di summary AE (AE1, AE2, ...).
export function deriveAeCountFromSummary(summary) {
  const fc = summary?.ae?.summary?.fuelConsumption || {};
  const rs = summary?.ae?.summary?.runningSeconds || {};
  let max = 0;
  [...Object.keys(fc), ...Object.keys(rs)].forEach((key) => {
    const match = /^AE(\d+)$/.exec(key);
    if (match) max = Math.max(max, Number(match[1]));
  });
  return max;
}

// gps & flowmeter sengaja TANPA functionNames: endpoint lama naruh sampel dasar
// di data[ts].speed dan data[ts].volumeFlowrate, bukan rata-rata. rpm sebaliknya,
// pakai AVGGT (rata-rata pas mesin nyala). Diseragamkan = angka bergeser diam-diam.
export const SERIES_REQUEST_PARAMS = {
  gps: {},
  rpm: { functionNames: ['AVGGT'], functionParams: ['@Rpm'] },
  flowmeter: {},
  ae: { functionNames: ['SUM'], functionParams: ['@Rpm'] },
  temperature: { functionNames: ['AVG'], functionParams: ['@Temp'] },
};

// Tabel Datalog baca jauh lebih banyak agregat daripada chart (koordinat akhir,
// jarak, gelombang, jam nyala, volume inventory). Sengaja DIPISAH dari
// SERIES_REQUEST_PARAMS: halaman chart yang udah rilis jangan ikut berubah
// request-nya cuma gara-gara halaman ini butuh lebih banyak.
export const DATALOG_SERIES_REQUEST_PARAMS = {
  gps: {
    functionNames: ['LAST', 'LAST', 'SUM', 'AVG', 'AVG'],
    functionParams: ['@Latitude', '@Longitude', '@Distance', '@WaveHeight', '@WavePeriod'],
  },
  rpm: { functionNames: ['AVGGT', 'COUNTGT'], functionParams: ['@Rpm', '@Rpm'] },
  flowmeter: { functionNames: ['FIRST'], functionParams: ['@VolInventory'] },
  ae: { functionNames: ['SUM'], functionParams: ['@Rpm'] },
  temperature: { functionNames: ['AVG'], functionParams: ['@Temp'] },
};

// Baris = bucket x channel. Buat rpm/ae channel-nya dari summary, BUKAN jumlah
// device: satu device rpm ngasih satu baris per PORT/STARBOARD.
export function rowsPerBucket({
  gpsCount, flowmeterCount, rpmTypes, aeCount, tempPositions,
} = {}) {
  return {
    gps: Math.max(Number(gpsCount) || 0, 1),
    rpm: Math.max((rpmTypes || []).length, 1),
    flowmeter: Math.max(Number(flowmeterCount) || 0, 1),
    ae: Math.max(Number(aeCount) || 0, 1),
    temperature: Math.max((tempPositions || []).length, 1),
  };
}

// Channel suhu dari tempDetails di body rollup, satu entri per grup+posisi —
// aset ber-exhaust punya dua device suhu, jadi PORT-nya kehitung dua kali dan
// anggaran barisnya emang segitu.
export function deriveTempPositionsFromSummary(summary) {
  const details = summary?.temperature?.summary?.tempDetails || {};
  const channels = [];
  Object.keys(details).forEach((group) => {
    Object.keys(details[group] || {}).forEach((pos) => channels.push(`${group}.${pos}`));
  });
  return channels;
}

export const SERIES_ROW_LIMIT = 6000;
// 80% dari batas keras, sisanya ruang buat channel yang baru mulai ngirim
// di tengah rentang.
export const SERIES_ROW_BUDGET = Math.floor(SERIES_ROW_LIMIT * 0.8);

export function maxBucketsFor(rows) {
  return Math.max(Math.floor(SERIES_ROW_BUDGET / Math.max(rows, 1)), 1);
}

export default seriesToSummaryData;
